import { _ as _slicedToArray } from '../unsorted/slicedToArray.js';
import { d as debounce } from '../unsorted/index5.js';
import { useState, useEffect } from 'react';

/**
 * This is a TypeScript function that uses the ResizeObserver API to track changes in the dimensions of
 * a specified HTML element and returns the updated dimensions.
 * @param elementRef - A reference to the DOM element that you want to observe for changes in size.
 * @param [debounceDuration=60] - `debounceDuration` is an optional parameter that specifies the
 * duration (in milliseconds) to wait before updating the dimensions after a resize event. It is used
 * to prevent the `setDimensions` function from being called too frequently and causing performance
 * issues. The default value is 60 milliseconds.
 * @returns The `useResizeObserver` hook returns an object containing the current dimensions of the
 * observed element, which includes its width and height.
 */
function useResizeObserver(elementRef) {
  var debounceDuration = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 60;
  var _useState = useState({
      width: 0,
      height: 0
    }),
    _useState2 = _slicedToArray(_useState, 2),
    dimensions = _useState2[0],
    setDimensions = _useState2[1];
  var debouncedSetDimensions = debounce(function (entries) {
    var _entry$borderBoxSize$, _elementRef$current, _entry$borderBoxSize$2, _elementRef$current2;
    var _entries = _slicedToArray(entries, 1),
      entry = _entries[0];
    var newWidth = ((_entry$borderBoxSize$ = entry.borderBoxSize[0]) === null || _entry$borderBoxSize$ === void 0 ? void 0 : _entry$borderBoxSize$.inlineSize) || (elementRef === null || elementRef === void 0 || (_elementRef$current = elementRef.current) === null || _elementRef$current === void 0 ? void 0 : _elementRef$current.offsetWidth);
    var newHeight = ((_entry$borderBoxSize$2 = entry.borderBoxSize[0]) === null || _entry$borderBoxSize$2 === void 0 ? void 0 : _entry$borderBoxSize$2.blockSize) || (elementRef === null || elementRef === void 0 || (_elementRef$current2 = elementRef.current) === null || _elementRef$current2 === void 0 ? void 0 : _elementRef$current2.offsetHeight);
    if (!newWidth || !newHeight) return;

    // @NOTE: IMPORTANT do not spam the state with useless updates
    if (newWidth === dimensions.width && newHeight === dimensions.height) {
      return;
    }
    setDimensions({
      width: newWidth,
      height: newHeight
    });
  }, debounceDuration, {
    leading: true
  });
  useEffect(function () {
    var observer = new ResizeObserver(debouncedSetDimensions);
    if (elementRef.current) {
      observer.observe(elementRef.current);
    }
    return function () {
      observer.disconnect();
      debouncedSetDimensions.cancel();
    };
    // @NOTE: we do not include `debouncedSetDimensions` in the dependency array -
    // becuase when we do, it causes the useEffect to trigger constantly
  }, [elementRef, debouncedSetDimensions]);
  return dimensions;
}

export { useResizeObserver };
//# sourceMappingURL=useResizeObserver.js.map
